import { useRef, useState } from 'react';
import { Alert, Divider, Flex, notification, Switch, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useUserService } from '../../services/userService';
import { notificationType } from '../../common/constants/notificationType';
import { spacing, textColor } from '../../theming';

const LABEL_ID = 'email-preferences-offers-label';
const DESCRIPTION_ID = 'email-preferences-offers-description';

// the API's answer is an assumption until checked: only a real boolean is ever shown as the stored choice
const readConsent = data => (typeof data?.marketingConsent === 'boolean' ? data.marketingConsent : null);

/**
 * The "Offers and news" switch. It saves on change and shows the server's value: while a save runs the switch is
 * disabled and shows the requested position, and afterwards it shows currentUser again, so a failed save reverts.
 */
export default function EmailPreferences({ disabled = false }) {
    const { t } = useTranslation(['settings']);
    const [api, contextHolder] = notification.useNotification();
    const { currentUser, patchCurrentUser } = useAuth();
    const { updateMarketingConsent, getUser } = useUserService();

    const [requested, setRequested] = useState(null);
    // one save at a time, even if two change events arrive before the re-render that disables the switch
    const savingRef = useRef(false);

    const stored = readConsent(currentUser);
    const isSaving = requested !== null;

    const toast = (type, key) => api[type]({ message: t(key), placement: 'top' });

    // the PUT may or may not have landed: ask the server instead of guessing, and keep the session as it is.
    // Quietly: a failed re-read would only repeat the error toast the save has already shown.
    const rereadConsent = async () => {
        try {
            const value = readConsent((await getUser({ quietErrors: true })).data);

            if (value !== null) {
                patchCurrentUser({ marketingConsent: value });
            }
            return value;
        } catch (error) {
            return null;
        }
    };

    const handleChange = async next => {
        if (savingRef.current || disabled) {
            return;
        }
        savingRef.current = true;
        setRequested(next);

        try {
            const value = readConsent((await updateMarketingConsent(next)).data);

            if (value === null) {
                // a 2xx without the field: the outcome is unknown until re-read
                const confirmed = await rereadConsent();
                toast(
                    confirmed === next ? notificationType.SUCCESS : notificationType.ERROR,
                    saveKey(confirmed === next)
                );
            } else {
                patchCurrentUser({ marketingConsent: value });
                toast(notificationType.SUCCESS, saveKey(true));
            }
        } catch (error) {
            if (!error?.response) {
                // no answer (network, timeout): the global interceptor has already said so
                await rereadConsent();
            } else if (error.response.status === 400) {
                // the interceptor stays silent on a 400, so the card says it
                toast(notificationType.ERROR, saveKey(false));
            }
            // any other status was already shown by the interceptor; the switch reverts to currentUser
        } finally {
            savingRef.current = false;
            setRequested(null);
        }
    };

    return (
        <Flex vertical gap={spacing[3]}>
            {contextHolder}
            {stored === null && <Alert type="warning" showIcon message={t('settings:email-preferences.unavailable')} />}
            <Flex justify="space-between" align="flex-start" gap={spacing[3]}>
                <Flex vertical>
                    <Typography.Text id={LABEL_ID} strong>
                        {t('settings:email-preferences.offers.label')}
                    </Typography.Text>
                    <Typography.Text id={DESCRIPTION_ID} style={{ color: textColor.secondary }}>
                        {t('settings:email-preferences.offers.description')}
                    </Typography.Text>
                </Flex>
                <Switch
                    checked={isSaving ? requested : stored === true}
                    loading={isSaving}
                    disabled={disabled || isSaving || stored === null}
                    onChange={handleChange}
                    aria-labelledby={LABEL_ID}
                    aria-describedby={DESCRIPTION_ID}
                    aria-busy={isSaving}
                />
            </Flex>
            <Divider style={{ margin: 0 }} />
            <Flex gap={spacing[1]} align="flex-start" style={{ color: textColor.secondary }}>
                <InfoCircleOutlined aria-hidden="true" style={{ marginTop: spacing[0.5] }} />
                <Typography.Text style={{ color: textColor.secondary }}>
                    {t('settings:email-preferences.service-note')}
                </Typography.Text>
            </Flex>
        </Flex>
    );
}

const saveKey = ok => (ok ? 'settings:email-preferences.saved' : 'settings:email-preferences.save-error');
